/**
 * Fuzzy Search Utilities
 * Scoring, filtering, and highlight matching for search inputs
 */

export function fuzzyScore(query: string, text: string): number {
  if (!query) return 1;

  const q = query.toLowerCase();
  const t = text.toLowerCase();

  if (t === q) return 1000;

  const substringIndex = t.indexOf(q);
  if (substringIndex !== -1) {
    return 500 + (100 - substringIndex);
  }

  let score = 0;
  let qi = 0;
  let lastMatchIndex = -1;
  let consecutiveBonus = 0;

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      score += 10;
      if (lastMatchIndex === ti - 1) {
        consecutiveBonus += 5;
        score += consecutiveBonus;
      } else {
        consecutiveBonus = 0;
      }
      if (ti === 0 || t[ti - 1] === ' ' || t[ti - 1] === '-' || t[ti - 1] === '_') {
        score += 15;
      }
      lastMatchIndex = ti;
      qi++;
    }
  }

  if (qi < q.length) return 0;
  score -= Math.floor(t.length / 10);
  return Math.max(1, score);
}

export function fuzzyFilter<T>(items: T[], query: string, getText: (item: T) => string): T[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return items;

  return items
    .map((item) => ({ item, score: fuzzyScore(trimmedQuery, getText(item)) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
}

export interface HighlightSegment {
  text: string;
  highlight: boolean;
}

export function highlightMatches(query: string, text: string): HighlightSegment[] {
  if (!query.trim()) {
    return [{ text, highlight: false }];
  }

  const q = query.toLowerCase();
  const t = text.toLowerCase();
  const result: HighlightSegment[] = [];

  const substringIndex = t.indexOf(q);
  if (substringIndex !== -1) {
    if (substringIndex > 0) {
      result.push({ text: text.slice(0, substringIndex), highlight: false });
    }
    result.push({ text: text.slice(substringIndex, substringIndex + q.length), highlight: true });
    if (substringIndex + q.length < text.length) {
      result.push({ text: text.slice(substringIndex + q.length), highlight: false });
    }
    return result;
  }

  let qi = 0;
  let currentSegment = '';
  let currentIsHighlight = false;

  for (let ti = 0; ti < text.length; ti++) {
    const isMatch = qi < q.length && t[ti] === q[qi];

    if (isMatch) {
      if (!currentIsHighlight && currentSegment) {
        result.push({ text: currentSegment, highlight: false });
        currentSegment = '';
      }
      currentSegment += text[ti];
      currentIsHighlight = true;
      qi++;
    } else {
      if (currentIsHighlight && currentSegment) {
        result.push({ text: currentSegment, highlight: true });
        currentSegment = '';
      }
      currentSegment += text[ti];
      currentIsHighlight = false;
    }
  }

  if (currentSegment) {
    result.push({ text: currentSegment, highlight: currentIsHighlight });
  }

  return result;
}
